<template>
    <div class="map-mobile-search" :class="{ 'map-desktop-search': isDesktopDevice }">
        <div class="search-input-wrapper" @click="searchVisible = true">
            <i class="left-icon fas fa-search" />
            <div class="search-placeholder line-clamp-1">
                {{ $t('map.search.placeholder') }}
            </div>

            <b-btn v-if="isMobileDevice" variant="text" pill class="right-action active-scale" size="xl" @click.stop="$emit('open-qr-scanner')">
                <i class="fal fa-qrcode-read" />
            </b-btn>
        </div>

        <e-mobile-modal v-if="isMobileDevice && cookieConsentAnswered" v-model="qrCodeOnboardingVisible" class="qr-code-onboarding-modal"
            panel-class="qr-code-onboarding-modal-panel" position="top"
            :close-on-backdrop="false" :close-on-escape="false" :hide-close="true" header-no-padding>
            <template #above-body>
                <b-btn variant="text" pill class="qr-code-onboarding-button right-action active-scale fa-beat" size="xl" style="--fa-beat-scale: 1.1; --fa-animation-duration: 1.25s;" @click.stop="openQrScanner">
                    <i class="fal fa-qrcode-read" />
                </b-btn>
            </template>

            <template #header>
                <div class="showcase-arrow" />
            </template>

            <div class="showcase-content showcase-content-animated">
                <div class="showcase-body">
                    <div class="showcase-label">
                        <i class="fal fa-qrcode f-14" />
                        <span>{{ $t('map.assistant.qrCodeScanner.onboardingLabel') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.assistant.qrCodeScanner.onboardingTitle') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.assistant.qrCodeScanner.onboardingIntro') }}
                    </div>
                </div>
                <div class="showcase-animation">
                    <b-img fluid class="showcase-image" :src="require('@/assets/img/qr-codes/qr-scan-hand.webp')" />
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block
                    @click="closeQrCodeOnboarding">
                    {{ $t('map.common.gotIt') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <map-mobile-search-modal
            :visible.sync="searchVisible" target-type="end"
            :initial-search="initialSearch"
            :latest-start-point="latestStartPoint"
            :latest-destination-point="latestDestinationPoint"
            :start-from-current-location="startFromCurrentLocation"
            :start-from-current-location-loading="startFromCurrentLocationLoading"
            :start-from-current-location-disabled="startFromCurrentLocationDisabled"
            @endpoint-selected="onEndpointSelected"
            @open-qr-scanner="$emit('open-qr-scanner')" />
    </div>
</template>

<script>
import MapMobileSearchModal from "@components/maps/mobile/map-mobile-search-modal.vue";
import EMobileModal from "@components/e-mobile-modal.vue";

export default {
    name: `map-mobile-search`,
    components: {MapMobileSearchModal, EMobileModal},
    props: {
        visible: {type: Boolean, default: false},
        mapItemSelected: { type: Object, default: null },
        latestStartPoint: { type: Object, default: null },
        latestDestinationPoint: { type: Object, default: null },
        startFromCurrentLocation: { type: Boolean, default: false },
        startFromCurrentLocationLoading: { type: Boolean, default: false },
        startFromCurrentLocationDisabled: { type: Boolean, default: false },
    },
    data() {
        return {
            qrCodeOnboarding: !localStorage.getItem(`map-mobile-search-qr-code-onboarding-shown`),
        };
    },
    computed: {
        cookieConsentAnswered() {
            return this.$store.state.cookieConsent.decided === true;
        },
        searchVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit(`update:visible`, value);
            }
        },
        initialSearch() {
            if (this.$route.query.search) {
                return decodeURIComponent(this.$route.query.search);
            } else {
                return null;
            }
        },
        qrCodeOnboardingVisible: {
            get() {
                return this.cookieConsentAnswered && this.qrCodeOnboarding;
            },
            set(value) {
                this.qrCodeOnboarding = value;
            }
        }
    },
    methods: {
        closeQrCodeOnboarding() {
            this.qrCodeOnboarding = false;
            localStorage.setItem(`map-mobile-search-qr-code-onboarding-shown`, `1`);
        },
        openQrScanner() {
            // this.closeQrCodeOnboarding();
            this.$emit(`open-qr-scanner`);
        },
        onEndpointSelected(event) {
            this.searchVisible = false;
            this.$emit(`endpoint-selected`, event);
        }
    }
};
</script>

<style lang="scss" scoped>
.map-mobile-search {
    position: absolute;
    left: 8px;
    right: 8px;
    top: 16px;
    z-index: 1;
    pointer-events: auto;
    cursor: pointer;

    &.map-desktop-search {
        position: fixed;
        top: 0;
        left: var(--sidebar-left);
        right: auto;

        width: var(--map-assistant-width);
        padding: 20px 20px 8px;
        border-right: 1px solid rgba(0, 0, 0, 0.1);
    }
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    border-radius: 999px;

    background: rgba(255, 255, 255, 0.96);
    -webkit-backdrop-filter: blur(20px) saturate(140%);
    backdrop-filter: blur(20px) saturate(140%);

    border: 1px solid var(--border-color);

    box-shadow: 0 2px 6px rgba(0,0,0,0.06), 0 10px 30px rgba(0,0,0,0.12);
}

.map-desktop-search .search-input-wrapper {
    box-shadow: none;
}

.left-icon {
    color: rgba(60, 60, 67, 0.72);
    font-size: 16px;
    margin-left: -4px;
    margin-right: 8px;
}

.right-action {
    font-size: 20px;
    margin-block: -19px;
    margin-right: -22px;
    border-radius: 999px;
    padding-block: 8px;
    color: black;
}

.search-placeholder {
    color: rgba(60, 60, 67, 0.72);
    font-size: 14px;
    font-weight: 500;
    flex: 1;
}

.qr-code-onboarding-modal {
    margin-top: 76px;
}

:deep(.qr-code-onboarding-modal-panel) {
    overflow: visible;
}

.qr-code-onboarding-button {
    position: absolute;
    top: -58px;
    right: 16px;
    background-color: white;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.showcase-arrow {
    position: absolute;
    width: 80px;
    height: 80px;
    top: -64px;
    right: 76px;
    transform: rotate(20deg) scaleX(-1) translate(-10px, 0);
    animation: showcase-content-appear 800ms ease-in-out;
    animation-delay: 300ms;
    animation-fill-mode: both;
    opacity: 1;

    background-color: white;
    -webkit-mask: url("~@/assets/icons/wayfinding/arrow-stylish.svg") no-repeat center / contain;
    mask: url("~@/assets/icons/wayfinding/arrow-stylish.svg") no-repeat center / contain;
}

@keyframes showcase-content-appear {
    0% {
        opacity: 0;
        transform: rotate(-20deg) scaleX(-1) translate(10px, 30px);
    }
    50% {
        opacity: 0.96;
        transform: rotate(24deg) scaleX(-1) translate(-12px, 0);
    }
    100% {
        opacity: 1;
        transform: rotate(20deg) scaleX(-1) translate(-10px, 0);
    }
}

.showcase-content {
    display: flex;
}

.showcase-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    text-align: left;
    width: 100%;
}

.showcase-animation {
    width: 180px;
    margin-right: -8px;
    margin-bottom: -16px;

    display: flex;
    justify-content: end;

    .showcase-image {
        width: 100%;
        height: auto;
        object-fit: contain;
        -webkit-mask-image: linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.72) 38%, rgba(0, 0, 0, 1) 72%);
        mask-image: linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.72) 38%, rgba(0, 0, 0, 1) 72%);
    }

    // @media screen and (min-width: 415px) {
    //     margin-left: 0;
    // }
}

.showcase-label {
    display: flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    padding: 7px 16px 7px 12px;
    border-radius: 999px;
    background: rgba(13, 110, 253, 0.12);
    font-size: 10pt;
    letter-spacing: 0.05rem;
    color: black;
}

.showcase-title {
    font-size: 17pt;
    line-height: 1.05;
    font-weight: 700;
    color: #111827;
    text-wrap: balance;
}

.showcase-description {
    font-size: 11pt;
    line-height: 1.45;
    color: rgba(17, 24, 39, 0.76);
    text-wrap: pretty;
}
</style>
